import { useState, useMemo, useRef } from "react";
import { useTranslate } from "@refinedev/core";

import { usePaginatedTable, useQueryParams } from "@shared/hooks";
import { ModalRef } from "@shared/components";
import { useFetchResourcesByIdsQuery } from "@shared/services/queries";
import { ApiResponse } from "@shared/types";
import { User } from "@modules/users/types";
import { Prescriber } from "@modules/prescribers/types";

export function usePrescriberList() {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const createPrescriberModalRef = useRef<ModalRef>(null);
  const editPrescriberModalRef = useRef<ModalRef>(null);

  const translate = useTranslate();
  const { getQueryParams } = useQueryParams();

  const { rows, loading, refetch, ...tableProps } = usePaginatedTable<Prescriber>({
    resource: "v1/prescriber_admin",
    filters: getQueryParams(),
  });

  const userIds = useMemo(
    () => [...new Set(rows?.map((prescriber) => prescriber.user).filter((id): id is number => !!id))],
    [rows]
  );

  const { data: users } = useFetchResourcesByIdsQuery<ApiResponse<User>>({
    resource: "v1/user_admin",
    ids: userIds,
  });

  const dataToExport = useMemo(() => {
    return selectedRows.length > 0
      ? rows.filter((row) => selectedRows.includes(row.id))
      : rows;
  }, [selectedRows, rows]);

  return {
    rows,
    loading,
    tableProps,
    users: users?.results,
    createPrescriberModalRef,
    editPrescriberModalRef,
    selectedRows,
    dataToExport,
    refetch,
    setSelectedRows,
    translate,
  };
}
